import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layers, Save, ChevronLeft, Trash2, Send, CheckCircle, X, Check, Settings } from 'lucide-react';
import { useDispatch } from 'react-redux';
import Editor from '@monaco-editor/react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { publishFlow, deprecateFlow } from '../../redux/whatsappFlowsSlice';

/* ─── Shared Tailwind class fragments (ported from the old inline `S` style object) ─── */
const CLS = {
  input: 'w-full px-[12px] py-[9px] border border-[#D8DDE6] rounded-[6px] text-[13px] box-border outline-none text-[#181818]',
  textarea: 'w-full px-[12px] py-[9px] border border-[#D8DDE6] rounded-[6px] text-[13px] box-border resize-y outline-none leading-[1.5] text-[#181818]',
  label: 'block text-[11px] font-bold text-[#706E6B] uppercase tracking-[0.05em] mb-[4px]',
  btnOutline: 'flex items-center gap-[6px] px-[16px] py-[6px] text-[12px] font-bold text-[#0176D3] bg-white border border-[#0176D3] rounded-[4px] cursor-pointer transition-all duration-150',
  btnPrimary: 'flex items-center gap-[6px] px-[20px] py-[6px] text-[12px] font-bold text-white bg-[#0176D3] border-0 rounded-[4px] cursor-pointer shadow-[0_2px_8px_rgba(1,118,211,0.3)] transition-all duration-150',
  btnDanger: 'flex items-center gap-[6px] px-[16px] py-[6px] text-[12px] font-bold text-[#BA0517] bg-[#FFF0F0] border border-[#FFC9C9] rounded-[4px] cursor-pointer',
  btnSuccess: 'flex items-center gap-[6px] px-[16px] py-[6px] text-[12px] font-bold text-white bg-[#2E844A] border-0 rounded-[4px] cursor-pointer',
  errorBox: 'bg-[#FFF0F0] border border-[#FFC9C9] rounded-[4px] px-[14px] py-[10px] text-[#BA0517] text-[12px]',
};

const statusBadgeClass = (status) => {
  const base = 'text-[10px] font-bold px-[10px] py-[3px] rounded-[4px] uppercase border';
  if (status === 'PUBLISHED') return `${base} bg-[#E1F5FE] text-[#0176D3] border-[#B9D6F3]`;
  if (status === 'DEPRECATED') return `${base} bg-[#FFF0F0] text-[#BA0517] border-[#FFC9C9]`;
  return `${base} bg-[#F4F6F9] text-[#706E6B] border-[#D8DDE6]`;
};

/* ─── Default empty flow JSON ─── */
const DEFAULT_FLOW_JSON = {
  version: "7.3",
  data_api_version: "3.0",
  routing_model: {
    WELCOME_SCREEN: []
  },
  screens: [
    {
      id: "WELCOME_SCREEN",
      title: "Welcome",
      terminal: true,
      data: {},
      layout: {
        type: "SingleColumnLayout",
        children: [
          {
            type: "Form",
            name: "welcome_form",
            children: [
              {
                type: "TextHeading",
                text: "Welcome to our service"
              },
              {
                type: "TextBody",
                text: "Please fill out the form below to continue."
              },
              {
                type: "TextInput",
                name: "first_name",
                label: "First Name",
                required: true
              },
              {
                type: "Footer",
                label: "Continue",
                "on-click-action": {
                  name: "complete",
                  payload: {
                    first_name: "${form.first_name}"
                  }
                }
              }
            ]
          }
        ]
      }
    }
  ]
};

/* ─── Component Preview renderer ─── */
const ComponentPreview = ({ comp }) => {
  switch (comp.type) {
    case 'TextHeading': return <div className="mb-[16px] text-[22px] font-bold text-black leading-[1.3]">{comp.text || 'Heading'}</div>;
    case 'TextSubheading': return <div className="mb-[16px] text-[16px] font-semibold text-[#1c1e21] leading-[1.4]">{comp.text || 'Subheading'}</div>;
    case 'TextBody': return <div className="mb-[16px] text-[15px] text-[#4b4f56] leading-[1.5]">{comp.text || 'Body text'}</div>;
    case 'TextCaption': return <div className="mb-[16px] text-[12px] text-[#8d949e] leading-[1.4]">{comp.text || 'Caption'}</div>;
    case 'TextInput':
      return (
        <div className="mb-[16px]">
          <input disabled placeholder={comp.label || 'Text input'} className="w-full px-[14px] py-[12px] border border-[#ccd0d5] rounded-[8px] text-[15px] box-border bg-[#fafafa] text-[#1c1e21]" />
          {comp['helper-text'] && <div className="text-[12px] text-[#8d949e] mt-[4px]">{comp['helper-text']}</div>}
        </div>
      );
    case 'TextArea':
      return (
        <div className="mb-[16px]">
          <textarea disabled placeholder={comp.label || 'Text area'} rows={3} className="w-full px-[14px] py-[12px] border border-[#ccd0d5] rounded-[8px] text-[15px] box-border resize-none bg-[#fafafa] text-[#1c1e21]" />
        </div>
      );
    case 'CheckboxGroup':
      return (
        <div className="mb-[16px]">
          <div className="text-[14px] font-semibold mb-[8px] text-[#1c1e21]">{comp.label}</div>
          {(comp['data-source'] || [{ id: 'opt1', title: 'Option 1' }]).map((o, idx) => (
            <div key={o.id || idx} className="flex items-center gap-[10px] py-[12px] border-b border-[#eee]">
              <div className="w-[20px] h-[20px] border-2 border-[#ccd0d5] rounded-[4px]"></div>
              <div className="text-[15px] text-[#1c1e21]">{o.title}</div>
            </div>
          ))}
        </div>
      );
    case 'RadioButtonsGroup':
      return (
        <div className="mb-[16px]">
          <div className="text-[14px] font-semibold mb-[8px] text-[#1c1e21]">{comp.label}</div>
          {(comp['data-source'] || [{ id: 'opt1', title: 'Option 1' }]).map((o, idx) => (
            <div key={o.id || idx} className="flex items-center gap-[10px] py-[12px] border-b border-[#eee]">
              <div className="w-[20px] h-[20px] border-2 border-[#ccd0d5] rounded-full"></div>
              <div className="text-[15px] text-[#1c1e21]">{o.title}</div>
            </div>
          ))}
        </div>
      );
    case 'Dropdown':
      return (
        <div className="mb-[16px]">
          <select disabled className="w-full px-[14px] py-[12px] border border-[#ccd0d5] rounded-[8px] text-[15px] box-border bg-[#fafafa] text-[#1c1e21] appearance-none">
            <option>{comp.label || 'Select…'}</option>
          </select>
        </div>
      );
    case 'DatePicker':
      return (
        <div className="mb-[16px]">
          <input type="date" disabled className="w-full px-[14px] py-[12px] border border-[#ccd0d5] rounded-[8px] text-[15px] box-border bg-[#fafafa] text-[#1c1e21]" />
        </div>
      );
    case 'Image':
      return (
        <div className="mb-[16px] bg-[#f0f2f5] rounded-[8px] h-[160px] flex items-center justify-center text-[13px] text-[#8d949e] overflow-hidden">
          {comp.src ? <img src={comp.src} alt="Preview" className="w-full h-full object-cover" /> : 'Image Placeholder'}
        </div>
      );
    case 'Footer':
      return (
        <div className="mt-auto pt-[20px] pb-[10px]">
          <button disabled className="w-full p-[14px] bg-[#00A884] text-white border-0 rounded-[24px] text-[15px] font-semibold">
            {comp.label || 'Submit'}
          </button>
        </div>
      );
    case 'Form':
      return (
        <div className="mb-[16px] flex flex-col w-full">
          {(comp.children || []).map((childComp, ci) => (
            <ComponentPreview key={ci} comp={childComp} />
          ))}
        </div>
      );
    case 'OptIn':
      return (
        <div className="mb-[16px] flex items-start gap-[10px]">
          <input type="checkbox" disabled className="mt-[3px]" />
          <div className="text-[13px] text-[#1c1e21] leading-[1.4]">{comp.label || 'Opt in'}</div>
        </div>
      );
    default:
      return <div className="mb-[16px] text-[12px] text-[#888] bg-[#f8f9fa] p-[10px] rounded-[6px] border border-dashed border-[#ccc]">[{comp.type} Component]</div>;
  }
};

/* ─── Visual Builder Components ─── */
const VisualComponentEditor = ({ comp, onChange, onDelete }) => {
  const handleChange = (field, value) => {
    onChange({ ...comp, [field]: value });
  };

  return (
    <div className="bg-white border border-[#D8DDE6] rounded-[8px] p-[16px] mb-[16px] relative">
      <button onClick={onDelete} className="absolute top-[16px] right-[16px] bg-transparent border-0 text-[#BA0517] cursor-pointer" title="Delete" aria-label="Delete">
        <Trash2 size={16} />
      </button>
      <div className="text-[13px] font-bold text-[#181818] mb-[12px]">{comp.type}</div>

      {['TextInput', 'Dropdown', 'RadioButtonsGroup'].includes(comp.type) && (
        <div className="flex items-center gap-[12px] mb-[12px]">
          <label className="text-[12px] font-semibold">Required</label>
          <input type="checkbox" checked={!!comp.required} onChange={(e) => handleChange('required', e.target.checked)} />
        </div>
      )}

      {comp.type === 'CheckboxGroup' && (
        <div className="flex items-center gap-[12px] mb-[12px]">
          <label className="text-[12px] font-semibold">Required (At least 1 item)</label>
          <input type="checkbox" checked={!!comp['min-selected-items']} onChange={(e) => {
            if (e.target.checked) handleChange('min-selected-items', 1);
            else {
              const newComp = { ...comp };
              delete newComp['min-selected-items'];
              onChange(newComp);
            }
          }} />
        </div>
      )}

      {(comp.type === 'TextHeading' || comp.type === 'TextSubheading' || comp.type === 'TextBody' || comp.type === 'TextCaption') && (
        <div>
          <label className="block text-[12px] font-semibold mb-[4px]">Text Content</label>
          <input className={CLS.input} value={comp.text || ''} onChange={(e) => handleChange('text', e.target.value)} />
        </div>
      )}

      {['TextInput', 'Dropdown', 'TextArea', 'CheckboxGroup', 'RadioButtonsGroup', 'OptIn', 'Footer'].includes(comp.type) && (
        <div className="mb-[12px]">
          <label className="block text-[12px] font-semibold mb-[4px]">Label</label>
          <input className={CLS.input} value={comp.label || ''} onChange={(e) => handleChange('label', e.target.value)} />
        </div>
      )}

      {['TextInput', 'Dropdown', 'TextArea', 'CheckboxGroup', 'RadioButtonsGroup'].includes(comp.type) && (
        <div>
          <label className="block text-[12px] font-semibold mb-[4px]">Field Name (Variable)</label>
          <input className={CLS.input} value={comp.name || ''} onChange={(e) => handleChange('name', e.target.value)} />
        </div>
      )}

      {comp.type === 'Image' && (
        <div>
          <label className="block text-[12px] font-semibold mb-[4px]">Image URL</label>
          <input className={CLS.input} value={comp.src || ''} onChange={(e) => handleChange('src', e.target.value)} />
        </div>
      )}

      {['Dropdown', 'CheckboxGroup', 'RadioButtonsGroup'].includes(comp.type) && (
        <div className="mt-[16px]">
          <label className="block text-[12px] font-semibold mb-[8px]">Options</label>
          {Array.isArray(comp['data-source']) ? (
            <div className="flex flex-col gap-[8px]">
              {comp['data-source'].map((opt, i) => (
                <div key={i} className="flex gap-[8px]">
                  <input
                    className={`${CLS.input} flex-1`}
                    placeholder="Option ID"
                    value={opt.id || ''}
                    onChange={(e) => {
                      const newDs = [...comp['data-source']];
                      newDs[i] = { ...newDs[i], id: e.target.value };
                      handleChange('data-source', newDs);
                    }}
                  />
                  <input
                    className={`${CLS.input} flex-[2]`}
                    placeholder="Option Title"
                    value={opt.title || ''}
                    onChange={(e) => {
                      const newDs = [...comp['data-source']];
                      newDs[i] = { ...newDs[i], title: e.target.value };
                      handleChange('data-source', newDs);
                    }}
                  />
                  <button
                    onClick={() => {
                      const newDs = [...comp['data-source']];
                      newDs.splice(i, 1);
                      handleChange('data-source', newDs);
                    }}
                    className="bg-transparent border-0 text-[#BA0517] cursor-pointer px-[4px] py-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newDs = [...comp['data-source'], { id: `opt_${Date.now()}`, title: 'New Option' }];
                  handleChange('data-source', newDs);
                }}
                className={`${CLS.btnOutline} mt-[4px] self-start`}
              >
                + Add Option
              </button>
            </div>
          ) : (
            <div className="text-[12px] text-[#888]">
              <div className="mb-[4px]">Using dynamic data source:</div>
              <input
                className={CLS.input}
                value={typeof comp['data-source'] === 'string' ? comp['data-source'] : ''}
                onChange={(e) => handleChange('data-source', e.target.value)}
              />
              <button
                onClick={() => handleChange('data-source', [{ id: 'opt1', title: 'Option 1' }])}
                className={`${CLS.btnOutline} mt-[8px]`}
              >
                Switch to Static Options
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const VisualBuilder = ({ previewScreens, selectedScreenId, onScreensChange }) => {
  const currentScreenIndex = previewScreens.findIndex(s => s.id === selectedScreenId);
  const currentScreen = currentScreenIndex >= 0 ? previewScreens[currentScreenIndex] : null;

  if (!currentScreen) return <div className="p-[20px]">Select a screen to edit</div>;

  const isFormWrapped = currentScreen.layout?.children?.[0]?.type === 'Form';
  const componentsList = isFormWrapped ? (currentScreen.layout.children[0].children || []) : (currentScreen.layout?.children || []);

  const updateComponents = (newComponentsList) => {
    const updatedScreens = [...previewScreens];
    const newScreen = { ...currentScreen };

    // Auto-sync form fields to Footer payload
    const formFields = {};
    newComponentsList.forEach(c => {
      if (c.name && ['TextInput', 'Dropdown', 'TextArea', 'CheckboxGroup', 'RadioButtonsGroup', 'OptIn'].includes(c.type)) {
        formFields[c.name] = `\${form.${c.name}}`;
      }
    });

    const finalComponentsList = newComponentsList.map(comp => {
      if (comp.type === 'Footer') {
        const actionName = comp['on-click-action']?.name || 'complete';

        // Only auto-sync payload if action is complete or data_exchange
        if (actionName === 'complete' || actionName === 'data_exchange') {
          const existingPayload = comp['on-click-action']?.payload || {};
          const newPayload = {};
          Object.keys(existingPayload).forEach(key => {
            if (typeof existingPayload[key] !== 'string' || !existingPayload[key].startsWith('${form.')) {
              newPayload[key] = existingPayload[key];
            }
          });
          return {
            ...comp,
            'on-click-action': {
              ...(comp['on-click-action'] || { name: actionName }),
              payload: { ...newPayload, ...formFields }
            }
          };
        }
      }
      return comp;
    });

    if (isFormWrapped) {
      const formComp = { ...newScreen.layout.children[0], children: finalComponentsList };
      newScreen.layout = { ...newScreen.layout, children: [formComp, ...newScreen.layout.children.slice(1)] };
    } else {
      newScreen.layout = { ...newScreen.layout, children: finalComponentsList };
    }
    updatedScreens[currentScreenIndex] = newScreen;
    onScreensChange(updatedScreens);
  };

  const handleComponentChange = (index, newComp) => {
    const newList = [...componentsList];
    newList[index] = newComp;
    updateComponents(newList);
  };

  const handleComponentDelete = (index) => {
    const newList = [...componentsList];
    newList.splice(index, 1);
    updateComponents(newList);
  };

  const handleAddComponent = (e) => {
    const type = e.target.value;
    if (!type) return;
    const newComp = { type };
    if (['TextInput', 'Dropdown', 'TextArea'].includes(type)) {
      newComp.label = `New ${type}`;
      newComp.name = `field_${Date.now()}`;
    }
    if (['Dropdown', 'CheckboxGroup', 'RadioButtonsGroup'].includes(type)) {
      newComp.label = `New ${type}`;
      newComp.name = `field_${Date.now()}`;
      newComp['data-source'] = [
        { id: 'opt1', title: 'Option 1' },
        { id: 'opt2', title: 'Option 2' }
      ];
    }
    if (type.startsWith('Text')) {
      newComp.text = `New ${type}`;
    }
    updateComponents([...componentsList, newComp]);
    e.target.value = "";
  };

  return (
    <div className="flex-1 p-[20px] overflow-y-auto">
      <div className="mb-[20px]">
        <h3 className="text-[16px] m-0 mb-[12px]">Components</h3>
        {componentsList.map((comp, idx) => (
          <VisualComponentEditor
            key={idx}
            comp={comp}
            onChange={(newComp) => handleComponentChange(idx, newComp)}
            onDelete={() => handleComponentDelete(idx)}
          />
        ))}
      </div>
      <div>
        <select className={`${CLS.input} bg-[#f8f9fa]`} onChange={handleAddComponent}>
          <option value="">+ Add Component</option>
          <option value="TextHeading">Heading</option>
          <option value="TextSubheading">Subheading</option>
          <option value="TextBody">Body Text</option>
          <option value="TextInput">Text Input</option>
          <option value="TextArea">Text Area</option>
          <option value="Dropdown">Dropdown</option>
          <option value="CheckboxGroup">Checkbox Group</option>
          <option value="RadioButtonsGroup">Radio Group</option>
          <option value="OptIn">Opt-In Checkbox</option>
          <option value="Footer">Submit Button</option>
          <option value="Image">Image</option>
        </select>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
const WhatsAppFlowBuilder = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ── state ── */
  const [flow, setFlow] = useState(null);
  const [flowName, setFlowName] = useState('');
  const [flowStatus, setFlowStatus] = useState('DRAFT');
  const [flowJson, setFlowJson] = useState(JSON.stringify(DEFAULT_FLOW_JSON, null, 2));
  const [jsonError, setJsonError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [builderMode, setBuilderMode] = useState('json');

  /* Preview State */
  const [previewScreens, setPreviewScreens] = useState([]);
  const [selectedScreenId, setSelectedScreenId] = useState('');

  /* Test Modal state */
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testBody, setTestBody] = useState('Hello! Please check out this flow.');
  const [testCTA, setTestCTA] = useState('Open Flow');
  const [testMode, setTestMode] = useState('draft');
  const [isSending, setIsSending] = useState(false);

  /* Settings Modal state */
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    isDynamic: false,
    endpointUri: '',
    triggerType: 'none',
    keywords: ''
  });

  /* ── load flow ── */
  useEffect(() => {
    if (flowId) loadFlow();
  }, [flowId]);

  const loadFlow = async () => {
    try {
      const res = await api.get(`/whatsapp-flows/${flowId}`);
      const f = res.data.flow || res.data;
      setFlow(f);
      setFlowName(f.name || '');
      setFlowStatus(f.status || 'DRAFT');
      const json = f.json_version || f.flowJson || DEFAULT_FLOW_JSON;
      const pretty = typeof json === 'string' ? json : JSON.stringify(json, null, 2);
      setFlowJson(pretty);
      parseScreensFromJSON(pretty);

      // Populate Settings form
      setSettingsForm({
        isDynamic: !!f.isDynamic,
        endpointUri: f.endpointUri || '',
        triggerType: f.trigger?.type || 'none',
        keywords: Array.isArray(f.trigger?.keywords) ? f.trigger.keywords.join(', ') : ''
      });
    } catch (e) {
      alertService.error('Error', e.response?.data?.error || 'Failed to load flow');
    }
  };

  /* ── parse JSON to update preview ── */
  const parseScreensFromJSON = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      setJsonError('');
      const screens = parsed.screens || [];
      setPreviewScreens(screens);

      // Auto-select first screen if current selection is invalid
      if (screens.length > 0 && (!selectedScreenId || !screens.find(s => s.id === selectedScreenId))) {
        setSelectedScreenId(screens[0].id);
      }
    } catch (e) {
      // Don't update preview screens if JSON is invalid, but show error
      setJsonError(`Invalid JSON: ${e.message}`);
    }
  };

  const handleEditorChange = (value) => {
    setFlowJson(value);
    parseScreensFromJSON(value);
  };

  const handleVisualScreensChange = (updatedScreens) => {
    setPreviewScreens(updatedScreens);
    try {
      const parsed = JSON.parse(flowJson);
      parsed.screens = updatedScreens;
      setFlowJson(JSON.stringify(parsed, null, 2));
      setJsonError('');
    } catch (e) {
      console.error("Error syncing visual builder to JSON", e);
    }
  };

  /* ── save config ── */
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const parsed = JSON.parse(flowJson);
      await api.patch(`/whatsapp-flows/${flowId}/json`, { json: parsed });
      await api.put(`/whatsapp-flows/${flowId}`, { name: flowName });
      alertService.toast('success', 'Flow configuration saved successfully.');
    } catch (e) {
      if (e instanceof SyntaxError) {
        alertService.error('Invalid JSON', 'Please fix JSON syntax errors before saving.');
      } else {
        alertService.error('Error', e.response?.data?.error || 'Failed to save flow');
      }
    } finally { setIsSaving(false); }
  };

  /* ── save settings ── */
  const handleSaveSettings = async () => {
    try {
      const payload = {
        isDynamic: settingsForm.isDynamic,
        endpointUri: settingsForm.isDynamic ? settingsForm.endpointUri : undefined,
        trigger: {
          type: settingsForm.triggerType,
          keywords: settingsForm.triggerType === 'keyword'
            ? settingsForm.keywords.split(',').map(k => k.trim()).filter(Boolean)
            : []
        }
      };
      await api.put(`/whatsapp-flows/${flowId}`, payload);
      alertService.toast('success', 'Settings Saved');
      setIsSettingsModalOpen(false);
    } catch (e) {
      alertService.error('Error', e.response?.data?.error || 'Failed to save settings');
    }
  };

  /* ── publish ── */
  const handlePublish = async () => {
    const confirmed = await alertService.confirm(
      'Publish Flow?',
      'Once published, the flow is live. You cannot edit it without deprecating first.',
      'warning',
      'Yes, Publish'
    );
    if (!confirmed) return;
    setIsPublishing(true);
    try {
      await dispatch(publishFlow(flowId)).unwrap();
      setFlowStatus('PUBLISHED');
      alertService.toast('success', 'Flow is now live.');
    } catch (e) {
      alertService.error('Publish Failed', String(e));
    } finally { setIsPublishing(false); }
  };

  /* ── deprecate ── */
  const handleDeprecate = async () => {
    const confirmed = await alertService.confirm(
      'Deprecate Flow?',
      'This will deactivate the flow. It can no longer be sent to users.',
      'warning',
      'Yes, Deprecate'
    );
    if (!confirmed) return;
    try {
      await dispatch(deprecateFlow(flowId)).unwrap();
      setFlowStatus('DEPRECATED');
      alertService.toast('info', 'Flow has been deprecated.');
    } catch (e) {
      alertService.error('Error', String(e));
    }
  };

  /* ── send test ── */
  const handleSendTest = async () => {
    if (!testRecipient) { alertService.toast('warning', 'Enter a recipient phone number'); return; }
    setIsSending(true);
    try {
      let initialScreenId = null;
      try {
        const parsed = JSON.parse(flowJson);
        if (!parsed.routing_model && parsed.screens?.length > 0) {
          initialScreenId = parsed.screens[0].id;
        }
      } catch (e) {
        // ignore JSON parse error here
      }

      await api.post(`/whatsapp-flows/${flowId}/send`, {
        recipient: testRecipient,
        body: testBody,
        cta: testCTA,
        mode: testMode,
        initialScreenId,
      });
      alertService.toast('success', 'Test flow message sent.');
      setIsTestModalOpen(false);
    } catch (e) {
      alertService.error('Send Failed', e.response?.data?.error || e.message);
    } finally { setIsSending(false); }
  };

  // Find the currently selected screen for preview
  const currentPreviewScreen = previewScreens.find(s => s.id === selectedScreenId) || previewScreens[0];

  /* ─────────────── RENDER ─────────────── */
  return (
    <div className="flex flex-col h-[calc(100vh-48px)] bg-[#F4F6F9] overflow-hidden">
      {/* HEADER */}
      <header className="flex items-center justify-between px-[20px] py-[8px] bg-white border-b border-[#D8DDE6] z-20 gap-[16px] shrink-0">
        <div className="flex items-center gap-[12px]">
          <button className="flex items-center justify-center w-[32px] h-[32px] rounded-[6px] bg-[#F4F6F9] border border-[#D8DDE6] cursor-pointer text-[#181818]" onClick={() => navigate('/dashboard/whatsapp-flows')} title="Back">
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center justify-center w-[36px] h-[36px] rounded-[8px] bg-[#0176D3] text-white shrink-0"><Layers size={18} /></div>
          <div>
            <input
              type="text"
              value={flowName}
              onChange={(e) => setFlowName(e.target.value)}
              className="bg-transparent text-[15px] font-bold border-0 border-b border-transparent outline-none w-[220px] text-[#181818] py-[2px] px-0 focus:border-[#0176D3]"
              placeholder="Flow Name"
            />
            <div className="text-[10px] text-[#0176D3] font-bold uppercase tracking-[0.08em] mt-[2px]">Flows Playground</div>
          </div>
          <div className="w-px h-[28px] bg-[#D8DDE6]" />
          <span className={statusBadgeClass(flowStatus)}>{flowStatus || 'DRAFT'}</span>
        </div>

        <div className="flex items-center gap-[10px]">
          <button className={CLS.btnOutline} onClick={() => setIsSettingsModalOpen(true)}>
            <Settings size={14} /> Settings
          </button>
          <button className={CLS.btnOutline} onClick={() => setIsTestModalOpen(true)}>
            <Send size={14} /> Send Test
          </button>
          <button className={CLS.btnPrimary} onClick={handleSave} disabled={isSaving || !!jsonError}>
            <Save size={14} /> {isSaving ? 'Saving…' : 'Save Draft'}
          </button>

          {(flowStatus === 'DRAFT' || !flowStatus) && (
            <button
              className={`${CLS.btnSuccess}${isPublishing ? ' opacity-60' : ''}`}
              onClick={handlePublish}
              disabled={isPublishing}
            >
              <CheckCircle size={13} /> {isPublishing ? 'Publishing…' : 'Publish'}
            </button>
          )}

          {flowStatus === 'PUBLISHED' && (
            <button className={CLS.btnDanger} onClick={handleDeprecate}>
              <Trash2 size={13} /> Deprecate
            </button>
          )}
        </div>
      </header>

      {/* SPLIT SCREEN MAIN AREA */}
      <main className="flex flex-1 overflow-hidden flex-row">

        {/* LEFT PANE */}
        <section className="flex-1 flex flex-col border-r border-[#D8DDE6] bg-white lg:min-w-[400px]">
          <div className="px-[16px] py-[10px] bg-white border-b border-[#D8DDE6] flex justify-between items-center">
            <div className="flex gap-[10px] items-center">
              <div className="text-[12px] font-bold text-[#181818] uppercase tracking-[0.05em]">Flow Definition</div>
              <div className="flex bg-[#F4F6F9] rounded-[4px] p-[2px] border border-[#D8DDE6]">
                <button
                  onClick={() => setBuilderMode('json')}
                  className={`border-0 rounded-[3px] px-[10px] py-[4px] text-[11px] font-semibold cursor-pointer ${builderMode === 'json' ? 'bg-white text-[#0176D3] shadow-[0_1px_3px_rgba(0,0,0,0.1)]' : 'bg-transparent text-[#706E6B] shadow-none'}`}
                >
                  JSON
                </button>
                <button
                  onClick={() => setBuilderMode('visual')}
                  className={`border-0 rounded-[3px] px-[10px] py-[4px] text-[11px] font-semibold cursor-pointer ${builderMode === 'visual' ? 'bg-white text-[#0176D3] shadow-[0_1px_3px_rgba(0,0,0,0.1)]' : 'bg-transparent text-[#706E6B] shadow-none'}`}
                >
                  Visual
                </button>
              </div>
            </div>
            {builderMode === 'json' && (
              <div>
                {jsonError && <span className="text-[11px] text-[#BA0517] font-semibold">Invalid JSON</span>}
                {!jsonError && <span className="text-[11px] text-[#2E844A] font-semibold"><Check size={12} className="align-middle mr-[4px]" />Valid JSON</span>}
              </div>
            )}
          </div>

          {builderMode === 'json' ? (
            <>
              <div className="flex-1 py-[16px] px-0">
                <Editor
                  height="100%"
                  defaultLanguage="json"
                  value={flowJson}
                  onChange={handleEditorChange}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    wordWrap: 'on',
                    formatOnPaste: true,
                    scrollBeyondLastLine: false,
                    padding: { top: 16 }
                  }}
                  theme="light"
                />
              </div>
              {jsonError && <div className={`${CLS.errorBox} m-[16px]`}>{jsonError}</div>}
            </>
          ) : (
            <VisualBuilder
              previewScreens={previewScreens}
              selectedScreenId={selectedScreenId}
              onScreensChange={handleVisualScreensChange}
            />
          )}
        </section>

        {/* RIGHT PANE: VISUAL PREVIEW */}
        <section className="flex-1 flex flex-col bg-[#EAE6DF] lg:min-w-[400px] relative">
          <div className="px-[16px] py-[10px] bg-white border-b border-[#D8DDE6] flex justify-between items-center">
            <div className="text-[12px] font-bold text-[#181818] uppercase tracking-[0.05em]">Visual Preview</div>
            <select
              value={selectedScreenId}
              onChange={(e) => setSelectedScreenId(e.target.value)}
              className="w-auto px-[10px] py-[4px] border border-[#D8DDE6] rounded-[6px] text-[12px] box-border outline-none text-[#181818]"
            >
              {previewScreens.length === 0 && <option value="">No screens</option>}
              {previewScreens.map(s => (
                <option key={s.id} value={s.id}>{s.title || s.id}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-center items-center p-[24px] overflow-y-auto flex-1">
            {currentPreviewScreen ? (
              <div className="w-full max-w-[340px] h-[680px] bg-white rounded-[40px] shadow-[0_15px_50px_rgba(0,0,0,0.15)] border-[10px] border-[#181818] overflow-hidden flex flex-col relative">
                <div className="h-[32px] bg-[#181818] flex items-center justify-center absolute top-0 left-1/2 -translate-x-1/2 w-[140px] rounded-bl-[16px] rounded-br-[16px] z-10">
                  <div className="w-[50px] h-[4px] bg-white/30 rounded-[2px]" />
                </div>
                {/* WhatsApp Chat Header */}
                <div className="flex items-center gap-[10px] pt-[40px] px-[16px] pb-[12px] bg-[#075E54] text-white">
                  <div className="w-[36px] h-[36px] rounded-full bg-white/20 flex items-center justify-center text-[14px] font-semibold">WA</div>
                  <div className="flex-1">
                    <div className="text-[15px] font-semibold">WhatsApp Business</div>
                    <div className="text-[12px] opacity-80">{currentPreviewScreen.title || currentPreviewScreen.id}</div>
                  </div>
                </div>

                {/* Phone Screen Canvas */}
                <div className="flex-1 p-[20px] overflow-y-auto bg-white">
                  <div className="flex flex-col min-h-full">
                    {(currentPreviewScreen.layout?.children || []).map((comp, ci) => (
                      <ComponentPreview key={ci} comp={comp} />
                    ))}
                    {(currentPreviewScreen.layout?.children || []).length === 0 && (
                      <div className="text-[13px] text-[#8d949e] text-center p-[40px]">Empty screen</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-[#706E6B]">
                <Layers size={48} color="#D8DDE6" className="mx-auto mb-[16px]" />
                <div>No valid screens found in JSON</div>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* TEST SEND MODAL */}
      {isTestModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white rounded-t-lg sm:rounded-lg w-full sm:w-[480px] max-h-[100dvh] overflow-y-auto p-4 sm:p-6 relative shadow-[0_2px_8px_rgba(0,0,0,0.16)]">
            <button className="absolute top-[16px] right-[16px] cursor-pointer text-[#706E6B]" onClick={() => setIsTestModalOpen(false)}>
              <X size={20} />
            </button>
            <div className="mb-[20px]">
              <div className="text-[18px] font-bold text-[#181818] mb-[4px]">Send Test Flow</div>
              <div className="text-[13px] text-[#706E6B]">
                Send this flow to a test recipient via WhatsApp. Ensure you save any recent changes before sending.
              </div>
            </div>

            <div className="mb-[16px]">
              <label className={CLS.label}>Recipient Phone Number</label>
              <input
                className={CLS.input}
                type="tel"
                placeholder="+1234567890 (with country code)"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
              />
            </div>

            <div className="mb-[16px]">
              <label className={CLS.label}>Body Text</label>
              <textarea
                className={`${CLS.textarea} min-h-[80px]`}
                placeholder="Message body shown with the flow button"
                value={testBody}
                onChange={(e) => setTestBody(e.target.value)}
              />
            </div>

            <div className="flex gap-[16px] mb-[24px]">
              <div className="flex-1">
                <label className={CLS.label}>CTA Button Text</label>
                <input
                  className={CLS.input}
                  type="text"
                  placeholder="e.g. Open Form"
                  value={testCTA}
                  onChange={(e) => setTestCTA(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <label className={CLS.label}>Mode</label>
                <select
                  className={`${CLS.input} h-[42px]`}
                  value={testMode}
                  onChange={(e) => setTestMode(e.target.value)}
                >
                  <option value="draft">Draft (test mode)</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            {flowStatus === 'DRAFT' && testMode === 'published' && (
              <div className={`${CLS.errorBox} mb-[16px]`}>
                The flow is in DRAFT status. Switch mode to "Draft" or publish the flow first.
              </div>
            )}

            <button
              className={`flex items-center justify-center gap-[6px] w-full py-[12px] px-0 text-[14px] font-bold text-white bg-[#0176D3] border-0 rounded-[4px] cursor-pointer shadow-[0_2px_8px_rgba(1,118,211,0.3)] transition-all duration-150${isSending ? ' opacity-60' : ''}`}
              onClick={handleSendTest}
              disabled={isSending}
            >
              <Send size={16} /> {isSending ? 'Sending…' : 'Send Test Message'}
            </button>
          </div>
        </div>
      )}
      {/* Settings Modal */}
      {isSettingsModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white rounded-t-lg sm:rounded-lg w-full sm:w-[480px] max-h-[100dvh] overflow-y-auto p-4 sm:p-6 relative shadow-[0_2px_8px_rgba(0,0,0,0.16)]">
            <X size={20} className="absolute top-[16px] right-[16px] cursor-pointer text-[#706E6B]" onClick={() => setIsSettingsModalOpen(false)} />
            <div className="flex items-center gap-[10px] mb-[20px]">
              <div className="flex items-center justify-center w-[36px] h-[36px] rounded-[8px] bg-[#0176D3] text-white shrink-0"><Settings size={18} /></div>
              <h2 className="text-[16px] font-bold m-0 text-[#181818]">Flow Settings</h2>
            </div>

            <div className="mb-[16px]">
              <label className={CLS.label}>Flow Type</label>
              <div className="flex gap-[12px]">
                <button
                  type="button"
                  className={`flex-1 p-[12px] border-2 rounded-[6px] cursor-pointer text-left ${settingsForm.isDynamic ? 'border-[#0176D3] bg-[#EBF5FE]' : 'border-[#D8DDE6] bg-white'}`}
                  onClick={() => setSettingsForm({ ...settingsForm, isDynamic: true })}
                >
                  <div className={`text-[12px] font-bold ${settingsForm.isDynamic ? 'text-[#0176D3]' : 'text-[#181818]'}`}>Dynamic (Data Exchange)</div>
                  <div className="text-[10px] text-[#706E6B] mt-[4px]">Real-time endpoint required</div>
                </button>
                <button
                  type="button"
                  className={`flex-1 p-[12px] border-2 rounded-[6px] cursor-pointer text-left ${!settingsForm.isDynamic ? 'border-[#0176D3] bg-[#EBF5FE]' : 'border-[#D8DDE6] bg-white'}`}
                  onClick={() => setSettingsForm({ ...settingsForm, isDynamic: false })}
                >
                  <div className={`text-[12px] font-bold ${!settingsForm.isDynamic ? 'text-[#0176D3]' : 'text-[#181818]'}`}>Static (Navigate)</div>
                  <div className="text-[10px] text-[#706E6B] mt-[4px]">Screen-to-screen navigation</div>
                </button>
              </div>
            </div>

            {settingsForm.isDynamic && (
              <div className="mb-[16px]">
                <label className={CLS.label}>Endpoint URI</label>
                <input
                  className={CLS.input}
                  type="url"
                  placeholder="https://..."
                  value={settingsForm.endpointUri}
                  onChange={(e) => setSettingsForm({ ...settingsForm, endpointUri: e.target.value })}
                />
              </div>
            )}

            <div className="mb-[16px]">
              <label className={CLS.label}>Trigger Type</label>
              <select
                className={`${CLS.input} h-[42px]`}
                value={settingsForm.triggerType}
                onChange={(e) => setSettingsForm({ ...settingsForm, triggerType: e.target.value })}
              >
                <option value="none">None</option>
                <option value="always">Always trigger</option>
                <option value="keyword">Specific Keywords</option>
              </select>
            </div>

            {settingsForm.triggerType === 'keyword' && (
              <div className="mb-[24px]">
                <label className={CLS.label}>Trigger Keywords (comma-separated)</label>
                <input
                  className={CLS.input}
                  type="text"
                  placeholder="e.g. support, help"
                  value={settingsForm.keywords}
                  onChange={(e) => setSettingsForm({ ...settingsForm, keywords: e.target.value })}
                />
              </div>
            )}

            <div className="flex justify-end gap-[12px] mt-[10px]">
              <button className="flex items-center gap-[6px] px-[16px] py-[6px] text-[12px] font-bold text-[#706E6B] bg-white border-0 rounded-[4px] cursor-pointer" onClick={() => setIsSettingsModalOpen(false)}>
                Cancel
              </button>
              <button className={CLS.btnPrimary} onClick={handleSaveSettings}>
                <Save size={14} /> Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppFlowBuilder;
